import styled from 'styled-components';
import colors, { opacify } from '../../utils/colors';
import { heights, outlines, zIndices } from '../../utils/theme';
import typography from '../../utils/typography';
import { subMenuTransitionDuration } from './SubMenuContent';
const { mainCopy, smallCopy } = typography;
const { blend, midnight, skyDarkest, lightGrey, lightBlue, offWhite, grey } = colors;
export const StyledSidebar = styled.aside.withConfig({
    shouldForwardProp: (prop) => !['width', 'broken', 'toggled', 'transitionDuration'].includes(prop),
})(({ width, broken, toggled, transitionDuration }) => [
    {
        boxSizing: 'border-box',
        position: 'relative',
        borderRight: `1px solid ${lightGrey}`,
        width: width,
        minWidth: width,
        height: '100%',
    },
    broken && {
        transition: `width, left, right, ${transitionDuration}ms`,
        position: 'fixed',
        top: 0,
        zIndex: zIndices.sidebar.shell,
        left: toggled ? 0 : `-${width}`,
    },
]);
export const StyledSidebarNavigation = styled.nav({
    position: 'relative',
    height: '100%',
    overflow: 'hidden auto',
    zIndex: zIndices.sidebar.navigation,
    backgroundColor: offWhite,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    justifyContent: 'space-between',
});
export const StyledSidebarBackdrop = styled.div({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: zIndices.sidebar.backdrop,
    backgroundColor: opacify(midnight, 0.72),
});
export const StyledMenu = styled.ul.withConfig({
    shouldForwardProp: (prop) => !['broken'].includes(prop),
})(({ broken }) => ({
    display: 'flex',
    flexDirection: 'column',
    listStyleType: 'none',
    gap: '0.25rem',
    margin: 0,
    flex: '1 1 auto',
    padding: broken ? '0.75rem' : '0.75rem 0.25rem',
}));
export const StyledMenuItem = styled.li.withConfig({
    shouldForwardProp: (prop) => !['isSelected'].includes(prop),
})(({ isSelected }) => ({
    position: 'relative',
    display: 'inline-block',
    width: '100%',
    listStyleType: 'none',
    '&[data-open="true"]': {
        backgroundColor: lightBlue,
    },
    ...(isSelected && {
        borderLeft: 'solid 5px #004C60',
        borderRadius: '5px',
        background: '#C8E6F1',
    }),
    '&:hover': {
        backgroundColor: lightBlue,
    },
}));
export const StyledMenuButton = styled.button.withConfig({
    shouldForwardProp: (prop) => !['broken', 'level'].includes(prop),
})(({ broken, level }) => [
    mainCopy,
    {
        boxSizing: 'border-box',
        cursor: 'pointer',
        width: '-webkit-fill-available',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '1rem 0.75rem',
        gap: '0.75rem',
        color: midnight,
        backgroundColor: 'inherit',
        textDecoration: 'none',
        textAlign: 'left',
        margin: '0 0.125rem',
        border: '2px solid transparent',
        borderRadius: '5px',
        '&[data-open="true"]': {
            backgroundColor: lightBlue,
        },
        '&[data-active="true"]': {
            backgroundColor: blend,
        },
        '&:focus-visible': {
            outlineOffset: 'inherit',
            outline: outlines.focus,
        },
        '&:hover': {
            backgroundColor: lightBlue,
        },
        svg: {
            flexShrink: 0,
            width: heights.menuIcon,
            height: heights.menuIcon,
            color: skyDarkest,
        },
    },
    level
        ? {
            marginLeft: `${1.75 * level}rem`,
            ...(!broken && {
                padding: '0.75rem 1rem',
                marginLeft: `${1.75 * (level - 1)}rem`,
            }),
        }
        : {
            borderLeft: `4px solid transparent`,
            '&[data-active="true"]': {
                backgroundColor: blend,
                borderLeft: `4px solid ${skyDarkest}`,
            },
            ...(!broken && {
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '0.75rem 0.5rem',
                gap: 0,
            }),
        },
]);
export const StyledMenuLabel = styled.span.withConfig({
    shouldForwardProp: (prop) => !['broken', 'level', 'shouldBreakSpaces', 'isDesktop'].includes(prop),
})(({ broken, level, shouldBreakSpaces, isDesktop }) => [
    broken && isDesktop ? mainCopy : smallCopy,
    {
        flexGrow: 1,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        whiteSpace: shouldBreakSpaces ? 'break-spaces' : 'nowrap',
    },
    !broken &&
        !level && {
        display: 'inline-block',
        textAlign: 'center',
    },
]);
export const StyledMenuExpandIcon = styled.span.withConfig({
    shouldForwardProp: (prop) => !['broken', 'open', 'level'].includes(prop),
})(({ open, broken, level }) => ({
    display: 'inline-block',
    transition: 'transform 0.3s ease 0s',
    borderRight: '2px solid currentcolor',
    borderBottom: '2px solid currentcolor',
    width: '5px',
    height: '5px',
    marginLeft: '0.25rem',
    flexShrink: 0,
    color: skyDarkest,
    transform: open && (broken || level) ? 'rotate(45deg)' : 'rotate(-45deg)',
}));
export const StyledSubMenuContent = styled.div.withConfig({
    shouldForwardProp: (prop) => !['open'].includes(prop),
})(({ open }) => [
    {
        display: 'none',
        overflow: 'hidden',
        transition: `height ${subMenuTransitionDuration}ms`,
    },
    open && {
        display: 'block',
        height: 'auto',
    },
]);
export const StyledSubMenuList = styled.ul({
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: '0.375rem 0',
    gap: '0.25rem',
    listStyleType: 'none',
});
export const StyledMenuItemDivider = styled.hr({
    margin: '0 -0.75rem 0.5rem',
    overflow: 'hidden',
    borderTop: `1px solid ${opacify(grey, 0.3)}`,
});
